import type { Stripe } from '@stripe/stripe-js';

import { completePublicCertificatePayment3DSAsync } from '@share/api';
import type { ICertificatePurchaseResult } from '@share/common-types';

export async function confirmPublicCertificate3DSIfNeeded({
  stripe,
  result,
  paymentMethodId,
  paymentToken,
  idempotencyKey,
  clientSecretOverride,
  requires3DSOverride,
  legIndex,
}: {
  stripe: Stripe;
  result: ICertificatePurchaseResult;
  paymentMethodId: string;
  paymentToken: string;
  idempotencyKey: string;
  /** When confirming a split-payment leg; use this client secret instead of the top-level one */
  clientSecretOverride?: string;
  /** When confirming a split-payment leg; use this requires3DS index instead of the top-level one */
  requires3DSOverride?: boolean;
  legIndex?: number;
}): Promise<ICertificatePurchaseResult> {
  let nextResult = result;
  const hasSecretOverride = clientSecretOverride !== undefined;
  const clientSecret = hasSecretOverride ? clientSecretOverride ?? '' : nextResult.clientSecret;
  const requires3DS =
    requires3DSOverride !== undefined ? requires3DSOverride : nextResult.requires3DS;
  const shouldConfirm = Boolean(clientSecret) && requires3DS;

  if (shouldConfirm) {
    const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
    });
    if (confirmError) {
      throw new Error(confirmError.message ?? '3D Secure verification failed.');
    }
    const { data: completeResult } = await completePublicCertificatePayment3DSAsync(
      paymentToken,
      { purchaseId: nextResult.purchaseId, legIndex },
      idempotencyKey,
    );
    nextResult = completeResult;
  }
  return nextResult;
}
